import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, CheckCircle } from "@phosphor-icons/react";
import { Reveal } from "../Reveal";
import { ProgramsBlock } from "./BnspPrograms";
import { useLanguage } from "../../context/LanguageContext";

const spans = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3"];

const ServiceModal = ({ service, onClose }) => {
  const { t } = useLanguage();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="service-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-10"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={service.title}
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-brand-paper w-full max-w-3xl max-h-[88vh] overflow-y-auto border border-black/15"
        onClick={(e) => e.stopPropagation()}
      >
        {service.image && (
          <div className="relative h-44 md:h-56 overflow-hidden">
            <img src={service.image} alt={service.title} className="w-full h-full object-cover grayscale" />
            <div className="absolute inset-0 bg-brand-green/20 mix-blend-multiply" />
          </div>
        )}
        <button
          data-testid="service-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 bg-brand-paper border border-black/15 hover:bg-brand-ink hover:text-brand-paper transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
          aria-label={t.services.close}
        >
          <X size={18} weight="bold" />
        </button>

        <div className="p-7 md:p-12">
          <span className="font-cabinet font-black text-5xl text-stroke">{service.num}</span>
          <h3 className="font-cabinet font-bold text-2xl sm:text-3xl tracking-tight mt-3 mb-5">
            {service.title}
          </h3>
          <p className="text-base leading-relaxed text-black/65 mb-8">{service.detail}</p>

          <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45 mb-4">
            {t.services.standardsLabel}
          </p>
          <div className="flex flex-wrap gap-2 mb-9">
            {service.tags.map((tag) => (
              <span key={tag} className="border border-black/20 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-black/70">
                {tag}
              </span>
            ))}
          </div>

          <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45 mb-4">
            {t.services.pointsLabel}
          </p>
          <ul className="flex flex-col gap-3 mb-10">
            {service.points.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-black/75">
                <CheckCircle size={20} weight="duotone" className="text-brand-green shrink-0 mt-0.5" />
                {p}
              </li>
            ))}
          </ul>

          {service.programs && (
            <div className="mb-10">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45 mb-4">
                {t.services.programsLabel}
              </p>
              <ProgramsBlock programs={service.programs} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-5">
            <button
              data-testid="service-modal-cta"
              onClick={() => {
                onClose();
                setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 150);
              }}
              className="group flex items-center gap-3 bg-brand-green text-white px-6 py-3.5 font-cabinet font-bold tracking-wide hover:bg-brand-ink transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              {t.services.detailCta}
              <ArrowUpRight size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <a
              data-testid="service-modal-page-link"
              href={`/layanan/${service.id}`}
              className="text-sm font-semibold text-black/60 hover:text-brand-green underline underline-offset-4 decoration-black/25 hover:decoration-brand-green transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              {t.servicePage.openPage} →
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Services = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-36 border-t border-black/10">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-red mb-6">
            — {t.services.label}
          </p>
          <div className="grid md:grid-cols-2 gap-8 items-end mb-14 md:mb-20">
            <h2 data-testid="services-title" className="font-cabinet font-black tracking-tight text-2xl sm:text-3xl lg:text-5xl leading-tight">
              {t.services.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-black/55 md:pl-10">
              {t.services.desc}
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {t.services.items.map((s, i) => (
            <Reveal key={s.id} delay={0.06 * (i % 3)} className={spans[i]}>
              <button
                data-testid={`service-card-${s.id}`}
                onClick={() => setActive(s)}
                className="group relative w-full h-full text-left bg-white border border-black/10 p-7 md:p-9 flex flex-col justify-between min-h-[240px] overflow-hidden transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-black/40 focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                {s.image && (
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <img src={s.image} alt="" aria-hidden="true" className="w-full h-full object-cover grayscale" />
                    <div className="absolute inset-0 bg-brand-paper/88 backdrop-blur-[2px]" />
                  </div>
                )}
                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-cabinet font-black text-4xl text-stroke">{s.num}</span>
                    <ArrowUpRight
                      size={22}
                      weight="bold"
                      className="text-black/30 transition-[color,transform] duration-300 group-hover:text-brand-red group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </div>
                  <h3 className="font-cabinet font-bold text-xl sm:text-2xl tracking-tight mb-3">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-black/55 mb-5">{s.desc}</p>
                </div>
                <div className="relative flex flex-wrap gap-1.5">
                  {s.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="border border-black/15 px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase text-black/50">
                      {tag}
                    </span>
                  ))}
                  {s.tags.length > 4 && (
                    <span className="px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase text-brand-green">
                      +{s.tags.length - 4}
                    </span>
                  )}
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <ServiceModal service={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
